import {
  Conditional,
  DealerLocatorFilterButton,
  DealerLocatorMapContainer,
  DealerLocatorMapToggle,
  DealerLocatorOptions,
  DealerStore,
  useDrawer
} from 'components/pcc-components';
import { GoogleMapsLoader } from 'components/google-maps-loader';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { Coordinates } from 'app/[lang]/[esite]/(core)/_header/hooks/use-locations';
import { useFormContext } from 'react-hook-form';
import { DEFAULT_NUM_MAP_RESULTS, SELECTED_STORE_ID } from '../constants';
import { sortClosestVisibleMapStores } from '../utils';
import { useDealerLocatorLoader } from 'components/dealer-locator/dealer-locator-loader-context';
import { Filters } from '../types';
import { DealerLocatorToggleButtons } from 'components/dealer-locator/dealer-location-toggle-buttons/DealerLocatorToggleButtons';
import { useEsiteCtx } from 'services/esite/context';

interface DealerLocatorOptionsWrapperProps {
  allFilteredDealerStoresByDistance: DealerStore[];
  filteredAndOrderedVisibleDealerStores: DealerStore[];
  locations: DealerStore[];
  onSearchThisAreaClick: (center: Coordinates) => void;
  isMapToggleChecked: boolean;
  setIsMapToggleChecked: (toggle: boolean) => void;
  isExtendMapToggleChecked: boolean;
  setIsExtendMapToggleChecked: (toggle: boolean) => void;
  isLoading: boolean;
  isGetLocationsError?: boolean;
  showNoStoresMapErrorMessage?: boolean;
  showNetworkErrorMessage: boolean;
  filters: Filters;
  areFiltersLoading: boolean;
  setAreFiltersLoading: (value: boolean) => void;
  setShouldCloseEntireDL: (value: boolean) => void;
  filtersDrawer: ReturnType<typeof useDrawer>;
  isHidingMaps: boolean;
  showInCountryToggle?: boolean;
}

export const DealerLocatorOptionsWrapper = ({
  locations,
  showInCountryToggle,
  allFilteredDealerStoresByDistance,
  filteredAndOrderedVisibleDealerStores,
  onSearchThisAreaClick,
  isMapToggleChecked,
  setIsMapToggleChecked,
  isLoading,
  isGetLocationsError,
  showNoStoresMapErrorMessage,
  areFiltersLoading,
  setAreFiltersLoading,
  filters,
  setShouldCloseEntireDL,
  filtersDrawer,
  showNetworkErrorMessage,
  isHidingMaps,
  isExtendMapToggleChecked,
  setIsExtendMapToggleChecked
}: DealerLocatorOptionsWrapperProps) => {
  const t = useTranslations();
  const { watch } = useFormContext();
  const { userCountryCode, dealerCode } = useEsiteCtx();
  const { isDealerLocatorLoading } = useDealerLocatorLoader();
  const selectedStoreId = watch(SELECTED_STORE_ID);

  const filteredAndOrderedVisibleMapDealerStores = useMemo(
    () =>
      allFilteredDealerStoresByDistance
        ? sortClosestVisibleMapStores(
            allFilteredDealerStoresByDistance?.slice(0, DEFAULT_NUM_MAP_RESULTS)
          )
        : undefined,
    [allFilteredDealerStoresByDistance]
  );

  const selectedStore = useMemo(
    () =>
      allFilteredDealerStoresByDistance?.find(
        (store) => store.id === Number(selectedStoreId)
      ),
    [selectedStoreId, allFilteredDealerStoresByDistance]
  );

  // Close the filters drawer when there are no errors applying filters
  useEffect(() => {
    if (filteredAndOrderedVisibleDealerStores.length && areFiltersLoading) {
      filtersDrawer.closeDrawer();
      setAreFiltersLoading(false);
      setShouldCloseEntireDL(true);
    }
  }, [
    filteredAndOrderedVisibleDealerStores,
    filtersDrawer,
    areFiltersLoading,
    setAreFiltersLoading,
    setShouldCloseEntireDL
  ]);

  // Turn map toggle off if there is a dealer locator service error or if the map search doesn't find a store
  useEffect(() => {
    if (
      isGetLocationsError ||
      showNoStoresMapErrorMessage ||
      showNetworkErrorMessage
    ) {
      setIsMapToggleChecked(false);
    }
  }, [
    isGetLocationsError,
    showNoStoresMapErrorMessage,
    showNetworkErrorMessage,
    setIsMapToggleChecked
  ]);

  const handleMapToggle = () => {
    setIsMapToggleChecked(!isMapToggleChecked);
  };

  const handleExtendMapToggle = () => {
    setIsExtendMapToggleChecked(!isExtendMapToggleChecked);
  };

  const filtersCounter = Object.values(filters || {}).filter(
    (filterValue) => filterValue === true
  ).length;

  const showOptions = !!locations?.length || isLoading;

  return (
    <>
      <Conditional test={showOptions}>
        <DealerLocatorOptions>
          <DealerLocatorToggleButtons>
            {!isHidingMaps && (
              <DealerLocatorMapToggle
                label={t('FYD_MAP_VIEW')}
                isChecked={isMapToggleChecked}
                setIsChecked={handleMapToggle}
                isDisabled={isDealerLocatorLoading}
              />
            )}

            {showInCountryToggle && (
              <DealerLocatorMapToggle
                label={t('EXTEND_STORE_LISTING_OUTSIDE_MY_COUNTRY')}
                isChecked={isExtendMapToggleChecked}
                setIsChecked={handleExtendMapToggle}
                isDisabled={isDealerLocatorLoading}
              />
            )}
          </DealerLocatorToggleButtons>

          <DealerLocatorFilterButton
            filtersDrawer={filtersDrawer}
            filtersCounter={filtersCounter}
            isDisabled={isDealerLocatorLoading}
          >
            {t('ARIA_LANDMARK_FILTER')}
          </DealerLocatorFilterButton>
        </DealerLocatorOptions>
      </Conditional>
      <Conditional
        test={
          !isHidingMaps &&
          isMapToggleChecked &&
          !!filteredAndOrderedVisibleMapDealerStores
        }
      >
        <GoogleMapsLoader useSmallLoader={false}>
          <DealerLocatorMapContainer
            userCountryCode={userCountryCode}
            dealerCode={dealerCode}
            dealerStores={filteredAndOrderedVisibleMapDealerStores}
            selectedStore={selectedStore}
            onSearchThisAreaClick={onSearchThisAreaClick}
            searchThisAreaBtnText={t('FYD_SEARCH_THIS_AREA')}
            loadingResults={isLoading}
          />
        </GoogleMapsLoader>
      </Conditional>
    </>
  );
};
